import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Text, ToggleButton, ToggleButtonGroup, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { UsageTabKey } from '../../../../types';
import { AggregationType, MeterType } from '../../../../types';
import { usageTabConfig } from '../../../constants/usageTabConfig';

import styles from './PickerBase.module.css';

type AggregationButtonGroupProps = {
  selectedKey?: AggregationType;
  handlePress: (type: AggregationType) => void;
  tab: UsageTabKey;
  meterType: MeterType;
};

export function AggregationButtonGroup({ selectedKey, handlePress, tab, meterType }: AggregationButtonGroupProps) {
  const { aggregationTypes } = usageTabConfig[tab];

  const hasRolling30day = aggregationTypes.includes(AggregationType.ROLLING_30DAY);
  const hasIncremental = aggregationTypes.includes(AggregationType.INCREMENTAL);
  const hasCumulative = aggregationTypes.includes(AggregationType.MONTH_TO_DATE);
  const hasAverage = aggregationTypes.includes(AggregationType.AVERAGE);

  return (
    <ToggleButtonGroup selectionMode="single" selectedKeys={selectedKey ? [selectedKey] : undefined}>
      <>
        {hasCumulative && (
          <TooltipTrigger>
            <ToggleButton
              className={styles.usageButton}
              id={AggregationType.MONTH_TO_DATE}
              onPress={() => handlePress(AggregationType.MONTH_TO_DATE)}
            >
              <Icon name="chart-area" size="small" slot="start-icon" />
              <Text>
                <Message id="billing.usageControls.cumulative" />
              </Text>
            </ToggleButton>
            <Tooltip placement="bottom">
              <Message id="billing.usageControls.countingResetsMonthly" />
            </Tooltip>
          </TooltipTrigger>
        )}

        {hasIncremental && (
          <TooltipTrigger>
            <ToggleButton
              className={styles.usageButton}
              id={AggregationType.INCREMENTAL}
              onPress={() => handlePress(AggregationType.INCREMENTAL)}
            >
              <Icon name="chart-bar" size="small" slot="start-icon" />
              <Text>
                <Message id="billing.usageControls.incremental" />
              </Text>
            </ToggleButton>
            {meterType === MeterType.UNIQUE && (
              <Tooltip placement="bottom">
                <Message id="billing.usageControls.uniqueCountingResets" />
              </Tooltip>
            )}
          </TooltipTrigger>
        )}

        {hasRolling30day && (
          <TooltipTrigger>
            <ToggleButton
              className={styles.usageButton}
              id={AggregationType.ROLLING_30DAY}
              onPress={() => handlePress(AggregationType.ROLLING_30DAY)}
            >
              <Icon name="chart-line" size="small" slot="start-icon" />
              <Text>
                <Message id="billing.usageControls.rolling30d" />
              </Text>
            </ToggleButton>
            <Tooltip placement="bottom">
              {meterType === MeterType.UNIQUE
                ? msg('billing.usageControls.rollingUniqueTooltip')
                : msg('billing.usageControls.rollingSumTooltip')}
            </Tooltip>
          </TooltipTrigger>
        )}

        {hasAverage && (
          <TooltipTrigger>
            <ToggleButton
              className={styles.usageButton}
              id={AggregationType.AVERAGE}
              onPress={() => handlePress(AggregationType.AVERAGE)}
            >
              <Icon name="chart-bar" size="small" slot="start-icon" />
              <Text>
                <Message id="billing.usageControls.average" />
              </Text>
            </ToggleButton>
            <Tooltip placement="bottom">
              <Message id="billing.usageControls.averageHourlyValue" />
            </Tooltip>
          </TooltipTrigger>
        )}
      </>
    </ToggleButtonGroup>
  );
}
